import type { File } from "$lib/wasm/file";
import { wasm } from "$lib/wasm/wasm";
import { createDeferredPromise } from "../../../../ceyper-common";
import { getCanvas } from "$lib/image/preview";

export async function downloadFileFrame(
    file: File,
    options: { name: string; scale: number; frame: number }
) {
    const scale = options.scale;
    const name = options.name;

    // create rust canvas
    const canvas = getCanvas(file);

    // create canvas
    const htmlCanvas = document.createElement("canvas");
    htmlCanvas.width = file.width * scale;
    htmlCanvas.height = file.height * scale;
    const ctx = htmlCanvas.getContext("2d")!;

    // create buffers
    const target = ctx.getImageData(0, 0, file.width * scale, file.height * scale);
    const target32 = new Uint32Array(target.data.buffer);
    const source32 = new Uint32Array(wasm.memory.buffer, canvas.data_ptr, file.width * file.height);

    canvas.draw_file_frame(file.internal, options.frame);
    // copy data to canvas
    for (let i = 0; i < target32.length; i++) {
        const x = i % target.width;
        const y = Math.floor(i / target.width);
        const sourceX = Math.floor(x / scale);
        const sourceY = Math.floor(y / scale);
        const sourceIndex = sourceY * file.width + sourceX;
        target32[i] = source32[sourceIndex];
    }
    ctx.putImageData(target, 0, 0);

    const blob = await htmlCanvasBlobAsync(htmlCanvas, "image/png");

    downloadBlob(blob, `${name}.png`);
}

export async function downloadFileAllFrames(file: File, options: { name: string; scale: number }) {
    const scale = options.scale;
    const name = options.name;

    // create rust canvas
    const canvas = getCanvas(file);

    // create canvas
    const htmlCanvas = document.createElement("canvas");
    htmlCanvas.width = file.width * scale;
    htmlCanvas.height = file.height * scale;
    const ctx = htmlCanvas.getContext("2d")!;

    // create buffers
    const target = ctx.getImageData(0, 0, file.width * scale, file.height * scale);
    const target32 = new Uint32Array(target.data.buffer);
    const source32 = new Uint32Array(wasm.memory.buffer, canvas.data_ptr, file.width * file.height);

    const files = [] as { name: string; blob: Blob }[];

    for (let frame = 0; frame < file.getFrames().length; frame++) {
        canvas.draw_file_frame(file.internal, frame);
        // copy data to canvas
        for (let i = 0; i < target32.length; i++) {
            const x = i % target.width;
            const y = Math.floor(i / target.width);
            const sourceX = Math.floor(x / scale);
            const sourceY = Math.floor(y / scale);
            const sourceIndex = sourceY * file.width + sourceX;
            target32[i] = source32[sourceIndex];
        }
        ctx.putImageData(target, 0, 0);

        const blob = await htmlCanvasBlobAsync(htmlCanvas, "image/png");
        files.push({
            name: `${name}_${frame}.png`,
            blob,
        });
    }

    await downloadZip(files, name);
}

function htmlCanvasBlobAsync(canvas: HTMLCanvasElement, type?: string) {
    const [promise, resolve, reject] = createDeferredPromise<Blob>();
    canvas.toBlob(async (blob) => {
        if (!blob) return reject();
        resolve(blob);
    }, type);
    return promise;
}

async function downloadZip(files: { name: string; blob: Blob }[], name: string) {
    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();

    for (let { name, blob } of files) {
        zip.file(name, blob);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    downloadBlob(zipBlob, `${name}.zip`);
}

function downloadBlob(blob: Blob, name: string) {
    const urlBlob = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = name;
    link.href = urlBlob;
    link.click();
    URL.revokeObjectURL(urlBlob);
}
